import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import {
  AreaChart,
  Label,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
  ResponsiveContainer,
} from 'recharts';
import ClipLoader from 'react-spinners/ClipLoader';

const PAGE_LIMIT = 1000;
const CHART_DATA_LIMIT = 10000;

const getRecordTime = (entry) =>
  new Date(entry.date ?? entry['Unnamed: 0'] ?? entry.timestamp).getTime();

// Normalize one record for consistent key access
const normalizeRecord = (d) => ({
  date: d.date ?? d['Unnamed: 0'],
  index1: d.index1 ?? d.mqe,
  index2: d.index2 ?? d.sp500,
  index3: d.index3 ?? d.hfri500,
});

function BacktestAreaChart({ theme, assetName, id }) {
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seriesConfig, setSeriesConfig] = useState({});

  // Fetch backtest data and normalize fallback keys
  const fetchAssets = useCallback(async (type) => {
    try {
      const url =
        type === 'assets'
          ? `${process.env.REACT_APP_BASE_URL}/assets/${id}/backtests?skip=0&limit=${CHART_DATA_LIMIT}`
          : `${process.env.REACT_APP_BASE_URL}/portfolios/${id}/backtests?skip=0&limit=${CHART_DATA_LIMIT}`;

      const response = await axios.get(url);
      if (response.status === 200) {
        const normalized = [...response.data]
          .sort((a, b) => getRecordTime(a) - getRecordTime(b))
          .map((d) => normalizeRecord(d));

        setComparisons(normalized);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setComparisons([]);
    }
  }, [id]);

  // Fetch dynamic series configs
  const fetchConfigs = useCallback(async (type) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/display-configs/${id}?scope=${type}`,
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_TOKEN}`,
          },
        }
      );
      if (response.status === 200) {
        setSeriesConfig(response.data);
      }
    } catch (err) {
      console.error('Error fetching configs:', err);
      setSeriesConfig({});
    }
  }, [id]);

  // Fetch whether ID belongs to portfolio or asset
  const fetchPortFolioData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/portfolios/?skip=0&limit=${PAGE_LIMIT}`
      );

      if (response.status === 200) {
        const isPortfolio = response?.data?.data?.some(
          (d) => d.id === id
        );
        await fetchAssets(isPortfolio ? 'portfolio' : 'assets');
        await fetchConfigs(isPortfolio ? 'portfolio' : 'asset');
      }
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
    } finally {
      setLoading(false);
    }
  }, [id, fetchAssets, fetchConfigs]);

  useEffect(() => {
    if (id) {
      fetchPortFolioData();
    }
  }, [id, fetchPortFolioData]);

  const hasSeriesData = (key) =>
    comparisons.some(
      (record) => record[key] !== null && record[key] !== undefined
    );

  const showIndex1 = hasSeriesData('index1');
  const showIndex2 = hasSeriesData('index2');
  const showIndex3 = hasSeriesData('index3');
  const showLegend = showIndex1 || showIndex2 || showIndex3;

  // Safe axis color for light/dark mode
  const axisFill = theme === 'light' ? undefined : '#ffffff';

  return (
    <>
      <h3
        className="color-ucla"
        style={{ textAlign: 'center', marginBottom: 10, marginTop: 15 }}
      >
        Backtest of MQE Algorithm on {assetName}
      </h3>
      <p className="center text-center mb-2 mx-5 color-primary">
        Cumulative performance from investing on MQE signals.
      </p>

      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 400,
          }}
        >
          <ClipLoader size={45} color="#005687" />
        </div>
      ) : (
        <ResponsiveContainer width="95%" height={400}>
          <AreaChart
            data={comparisons}
            margin={{ top: 10, right: 30, left: 30, bottom: 30 }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="45%" stopColor="#FBC138" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#FBC138" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorKv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#005687" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#005687" stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="date"
              tick={{ textAnchor: 'end', angle: -30, fill: axisFill }}
              height={80}
              minTickGap={30}
            >
              <Label
                value="Date"
                offset={-20}
                position="bottom"
                style={{ fill: axisFill }}
              />
            </XAxis>

            <YAxis
              tick={{ fill: axisFill }}
              label={{
                fill: axisFill,
                value: seriesConfig?.y_axis || '',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: axisFill },
              }}
            />

            <Tooltip wrapperClassName="custom-tooltip" />
            {showLegend && <Legend />}
            <Brush dataKey="date" height={25} stroke="#005687" />

            {showIndex1 && (
              <Area
                connectNulls
                type="monotone"
                dataKey="index1"
                name={seriesConfig?.index1 || 'MQE Strategy'}
                stroke="#005687"
                fill="url(#colorKv)"
              />
            )}
            {showIndex2 && (
              <Area
                connectNulls
                type="monotone"
                dataKey="index2"
                name={seriesConfig?.index2 || 'S&P 500'}
                stroke="#5a2cf4"
                fill="url(#colorUv)"
              />
            )}
            {showIndex3 && (
              <Area
                connectNulls
                type="monotone"
                dataKey="index3"
                name={seriesConfig?.index3 || 'HFRI 500'}
                stroke="#FBC138"
                fill="url(#colorPv)"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </>
  );
}

export default BacktestAreaChart;
